import { SitesV3ResponseDto } from '@/entities/sites/api/types';
import { GetStatisticsV3Params } from '@/entities/statistics/api/types';
import { publisherApi } from '@/shared/api/publisherApi';
import { formatDateToEU } from '@/shared/lib';

import type {
  AvailableBalanceDto,
  BalanceDto,
  IPartnersPayment,
  ReferralPaymentsBalanceV3Response,
  ReferralPaymentsV3Response,
  ReferralResponseDto,
} from './types';

// Helper function to transform V3 response to old format
const transformV3ToOldFormat = (
  v3Response: ReferralPaymentsV3Response,
  group_by: string
): ReferralResponseDto => {
  const { data } = v3Response;

  return {
    payments: data.items.map(
      (item) =>
        ({
          [group_by === 'sid' ? 'site' : 'date']:
            // group_by === 'sid' ? item.key : item.key,
            group_by === 'sid' ? item.key : formatDateToEU(item.key),
          sum: item.sum,
        }) as unknown as IPartnersPayment
    ),
    totalCount: data.total_count,
    page: data.page_index,
    pageSize: data.page_size as 15 | 50 | 100,
    totalPages: data.total_pages,
    totals: {
      sum: data.summary.sum,
    },
  };
};

export const referralsApi = publisherApi.injectEndpoints({
  endpoints: (build) => ({
    getReferralBalance: build.query<BalanceDto['referralBalance'], void>({
      query: () => {
        const url = `/v2/widgets/referrals/balance`;

        return { url };
      },
      transformResponse: ({ referralBalance }: BalanceDto) => referralBalance,
    }),
    getReferralAvailableBalance: build.query<
      AvailableBalanceDto['availableReferralBalance'],
      void
    >({
      query: () => {
        const url = `/v2/widgets/referrals/available_balance`;

        return { url };
      },
      transformResponse: ({ availableReferralBalance }: AvailableBalanceDto) =>
        availableReferralBalance,
    }),

    // V3
    getReferralPaymentsV3: build.query<
      ReferralResponseDto,
      GetStatisticsV3Params
    >({
      query: (params) => {
        const url = `/v3/referral-payments`;

        return { url, params };
      },
      transformResponse: (
        response: ReferralPaymentsV3Response,
        _,
        { group_by }
      ) => {
        return transformV3ToOldFormat(response, group_by);
      },
      providesTags: ['Referrals'],
    }),

    getReferralSitesV3: build.query<SitesV3ResponseDto['data'], void>({
      query: () => {
        const url = `/v3/sites/search`;

        return {
          url,
          params: {
            page: 1,
            limit: 1000,
            is_ref: true,
          },
        };
      },
      transformResponse: (response: SitesV3ResponseDto) => response.data,
      providesTags: [{ type: 'ReferralSites' }],
    }),

    getReferralBalanceV3: build.query<
      ReferralPaymentsBalanceV3Response['data'],
      void
    >({
      query: () => {
        const url = `/v3/referral-payments/balance`;

        return { url };
      },
      transformResponse: (response: ReferralPaymentsBalanceV3Response) =>
        response.data,
    }),
  }),
});

export const {
  useGetReferralBalanceQuery,
  useGetReferralAvailableBalanceQuery,
  // V3
  useGetReferralSitesV3Query,
  useGetReferralPaymentsV3Query,
  useGetReferralBalanceV3Query,
} = referralsApi;
